import { useRef, useState } from "react";
import domtoimage from "dom-to-image";
import DealerReviewFilter from "../../Components/Filters/DealerReviewFilter";
import FinancierMatrix from "./DealerReviewStatices/FinancierMatrix";

import ServiceTable from "./DealerReviewStatices/Services";
import ServiceTablePdf from "./DealerReviewStatices/ServicesPdf";
import Cpsc from "./DealerReviewStatices/Cpsc";
import ManpowerTable from "./DealerReviewStatices/Manpower";
import Mismatch from "./DealerReviewStatices/Mismatch";
import GtmeTable from "./DealerReviewStatices/GtmeTable";
import Vaspenetration from "./DealerReviewStatices/Vaspenetration";
import StockTable from "./DealerReviewStatices/StockAnalysis";
import { DealerReviewCharts } from "./DealerReviewStatices/DealerCharts";
import WarScore from "./DealerReviewStatices/WarScore";
import Samridhi from "./DealerReviewStatices/Samridhi";
import PsfSales from "./DealerReviewStatices/PsfSales";
import ServiceAdoption from "./DealerReviewStatices/ServiceAdoption";
import { DigitalSales } from "./DealerReviewStatices/DigitalSales";

import { FaFileDownload } from "react-icons/fa";
import Tata from "../../Artifacts/tata-motors-white.svg";
import PdfName from "./DealerReviewStatices/PdfName";
import WaterMark from "../../Components/WaterMark/WaterMark";
import FloatingButtonWithModal from "../../Components/Buttons/FloatingButtonWithModal";
import { DseMatrics } from "./DealerReviewStatices/DseMatrics";

import TopCollabaxSection from "./DealerReviewStatices/TopCollabaxSection";

const DealerReview = () => {
  const [isExporting, setIsExporting] = useState(false);

  const contentRef1 = useRef();
  const contentRef2 = useRef();
  const contentRef3 = useRef();
  const contentRef4 = useRef();
  const headingRef = useRef();
  const cpscSectionRef = useRef();
  const filterRef = useRef();
  const ProductLineRef = useRef([]);
  const ProductLine2Ref = useRef([]);

  const financierRefs = useRef([]);
  const gtmeRefs = useRef([]);
  const vasRefs = useRef([]);
  const cpscRefs = useRef([]);

  const productLines = [
    "HCV Trucks",
    "ILMCV Trucks",
    "CV Passenger",
    "SCV & PU",
  ];
  const CpscproductLines = ["HCV Trucks", "SCV & PU"];

  // --- helpers ----------------------------------------------------
  const nextFrame = () =>
    new Promise((resolve) => requestAnimationFrame(() => resolve()));

  const tinyPause = (ms = 0) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  // capture node -> { dataUrl, width, height }
  const captureNode = async (node) => {
    const dataUrl = await domtoimage.toPng(node);
    const img = new Image();
    img.src = dataUrl;
    await new Promise((res, rej) => {
      img.onload = () => res();
      img.onerror = rej;
    });
    return {
      dataUrl,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
    };
  };

  // ----------------------------------------------------------------

  const downloadPDF = async () => {
    setIsExporting(true);
    await nextFrame();

    // Find all elements with w-[416px]
    const fixedWidthElements = document.querySelectorAll(".w-\\[416px\\]");

    // UI state changes (exactly like your original)
    headingRef.current.style.display = "flex";
    cpscSectionRef.current.style.display = "block";
    filterRef.current?.classList.remove("hidden");
    ProductLineRef.current.forEach((el) => el?.classList.remove("hidden"));
    ProductLine2Ref.current.forEach((el) => el?.classList.add("hidden"));

    const drillTables = document.querySelectorAll(".drilltable");
    drillTables.forEach((el) => el.classList.add("active"));

    // Remove the fixed width class before capture
    fixedWidthElements.forEach((el) => el.classList.remove("w-[416px]"));

    try {
      // Capture sections in the same order; insert tiny pauses to keep UI responsive
      const pages = [];

      // 1) Heading (no addPage)
      pages.push({
        ...(await captureNode(headingRef.current)),
        addPage: false,
      });
      await tinyPause(0);

      pages.push({ ...(await captureNode(filterRef.current)), addPage: false });
      await tinyPause(0);
      pages.push({
        ...(await captureNode(contentRef1.current)),
        addPage: false,
      });
      await tinyPause(0);
      pages.push({
        ...(await captureNode(contentRef2.current)),
        addPage: true,
      });
      await tinyPause(0);
      pages.push({
        ...(await captureNode(contentRef3.current)),
        addPage: true,
      });
      await tinyPause(0);
      pages.push({
        ...(await captureNode(contentRef4.current)),
        addPage: true,
      });

      // Build PDF in a worker (jsPDF runs off the main thread)
      const worker = new Worker(
        new URL("../../workers/pdfWorker.js", import.meta.url),
      );
      const watermarkEl = document.getElementById("pdf-watermark-text");
      const watermarkText = watermarkEl
        ? watermarkEl.innerText.trim()
        : "CONFIDENTIAL";

      worker.postMessage({ pages, watermarkText });

      worker.onmessage = (e) => {
        const { ok, blob, error } = e.data || {};
        if (!ok) {
          console.error("PDF worker error:", error);
          // Restore UI on failure
          headingRef.current.style.display = "none";
          filterRef.current?.classList.add("hidden");
          cpscSectionRef.current.style.display = "flex";
          ProductLineRef.current.forEach((el) => el?.classList.add("hidden"));
          ProductLine2Ref.current.forEach((el) =>
            el?.classList.remove("hidden"),
          );
          drillTables.forEach((el) => el.classList.remove("active"));
          fixedWidthElements.forEach((el) => el.classList.add("w-[416px]"));
          setIsExporting(false);
          worker.terminate();
          return;
        }

        // Download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Snapshot for Monthly Review.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);

        // Restore UI
        headingRef.current.style.display = "none";
        cpscSectionRef.current.style.display = "flex";
        filterRef.current?.classList.add("hidden");
        ProductLineRef.current.forEach((el) => el?.classList.add("hidden"));
        ProductLine2Ref.current.forEach((el) => el?.classList.remove("hidden"));
        drillTables.forEach((el) => el.classList.remove("active"));
        fixedWidthElements.forEach((el) => el.classList.add("w-[416px]"));

        setIsExporting(false);
        worker.terminate();
      };
    } catch (err) {
      console.error("PDF generation failed:", err);

      // Restore UI on error
      headingRef.current.style.display = "none";
      cpscSectionRef.current.style.display = "flex";
      filterRef.current?.classList.add("hidden");
      ProductLineRef.current.forEach((el) => el?.classList.add("hidden"));
      ProductLine2Ref.current.forEach((el) => el?.classList.remove("hidden"));
      drillTables.forEach((el) => el.classList.remove("active"));
      fixedWidthElements.forEach((el) => el.classList.add("w-[416px]"));

      setIsExporting(false);
    }
  };

  return (
    <div className="flex flex-col  p-2 w-full h-full">
      <div id="pdf-watermark-text">
        <WaterMark />
      </div>
      {isExporting && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center">
          <div className="bg-white px-5 py-4 rounded-lg shadow-md flex items-center gap-3">
            <div className="w-6 h-6 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
            <span className="font-semibold">Generating PDF…</span>
          </div>
        </div>
      )}

      <div className="print:hidden flex flex-col md:flex-row justify-between gap-1 w-full">
        <div className="w-full md:w-[95%]">
          <DealerReviewFilter />
        </div>
        <button
          type="button"
          onClick={downloadPDF}
          title="Export PDF"
          className="bg-[#2A5775] w-full md:w-[5%] text-center justify-center flex md:flex-col text-white p-2  items-center text-xs font-semibold gap-2 rounded-md"
        >
          Export PDF
          <FaFileDownload className="text-lg" />
        </button>
      </div>

      <div ref={contentRef1} id="pdf-content-1">
        <div
          ref={headingRef}
          style={{ display: "none" }}
          className="bg-[#2A5775] text-xl flex items-center gap-[22rem] text-center w-full text-white rounded-md font-bold my-2 p-2 "
        >
          <img
            src={Tata}
            alt="Tata Motors"
            width="300"
            height="80"
            loading="eager"
            className="w-36 h-[42px]"
          />
          Snapshot for Monthly Review
        </div>

        <div ref={filterRef} className="mt-2 hidden">
          <PdfName />
        </div>

        <section className="md:w-full mb-2 w-[416px]">
          <TopCollabaxSection show={isExporting} />
        </section>

        <section className="p-2 px-0 pt-0 w-full">
          <DealerReviewCharts />
        </section>
      </div>

      <div ref={contentRef2} id="pdf-content-2">
        <section className="p-2 px-0 pt-0 md:w-full w-[416px]">
          <div ref={(el) => (ProductLine2Ref.current[0] = el)}>
            <FinancierMatrix />
          </div>
          <div
            ref={(el) => (ProductLineRef.current[0] = el)}
            className="hidden"
          >
            {productLines.map((line, index) => (
              <div
                className="p-1"
                key={line}
                ref={(el) => (financierRefs.current[index] = el)}
              >
                <FinancierMatrix productLine={line} />
              </div>
            ))}
          </div>
        </section>
        <section
          className="p-2 px-0 pt-0 w-full md:flex gap-2 block "
          ref={cpscSectionRef}
        >
          <div
            className="md:w-1/2 w-full"
            ref={(el) => (ProductLine2Ref.current[3] = el)}
          >
            <Cpsc />
          </div>
          <div
            ref={(el) => (ProductLineRef.current[3] = el)}
            className="hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {CpscproductLines.map((line, index) => (
                <div
                  className="w-full"
                  key={line}
                  ref={(el) => (cpscRefs.current[index] = el)}
                >
                  <Cpsc productLine={line} show={isExporting} />
                </div>
              ))}
            </div>
          </div>

          <div className="md:w-1/2 w-full mt-2 md:mt-0">
            <Mismatch />
          </div>
        </section>
      </div>

      <div ref={contentRef3} id="pdf-content-3">
        <section className="p-2 px-0 pt-0 md:w-full w-[416px]">
          {!isExporting && <ServiceTable />}
          {isExporting && <ServiceTablePdf />}
        </section>

        <section className="p-2 px-0 md:pt-0 md:w-full w-[416px]">
          <div ref={(el) => (ProductLine2Ref.current[1] = el)}>
            <GtmeTable />
          </div>
          <div
            ref={(el) => (ProductLineRef.current[1] = el)}
            className="hidden"
          >
            {productLines.map((line, index) => (
              <div key={line} ref={(el) => (gtmeRefs.current[index] = el)}>
                <GtmeTable productLine={line} />
              </div>
            ))}
          </div>
        </section>
      </div>
      <div ref={contentRef4} id="pdf-content-4">
        <section className="p-2 px-0 md:pt-0 md:flex block gap-2">
          <div className="md:w-1/2">
            <WarScore show={isExporting} />
          </div>
          <div className="md:w-1/2 w-full mt-2 md:mt-0">
            <ServiceAdoption />
          </div>
        </section>

        <section className="p-2 px-0 pt-0 md:w-full w-[416px]">
          <div ref={(el) => (ProductLine2Ref.current[2] = el)}>
            <Vaspenetration />
          </div>
          <div
            ref={(el) => (ProductLineRef.current[2] = el)}
            className="hidden mb-1"
          >
            {productLines.map((line, index) => (
              <div key={line} ref={(el) => (vasRefs.current[index] = el)}>
                <Vaspenetration productLine={line} />
              </div>
            ))}
          </div>
        </section>

        <section className="p-2 px-0 md:pt-0 md:flex block gap-2">
          <div className="md:w-[55%] w-[416px]">
            <PsfSales />
          </div>
          <div className="md:w-[45%] w-full mt-2 md:mt-0">
            <Samridhi />
          </div>
        </section>

        <section className="md:w-full w-[416px] md:flex block gap-2">
          <div className="md:w-[65%] w-full">
            <ManpowerTable show={isExporting} />
          </div>
          <div className="md:w-[35%] w-full">
            <DseMatrics />
          </div>
        </section>

        <section className="p-2 px-0  md:flex block gap-2">
          <div className="md:w-1/2 w-[416px]">
            <DigitalSales />
          </div>
          <div className="md:w-1/2 w-[416px] mt-2 md:mt-0 ">
            <StockTable />
          </div>
        </section>
      </div>
      <FloatingButtonWithModal />
    </div>
  );
};

export default DealerReview;
